import { toDestinationAppPath } from "@studio/Helpers/destination-app-routes.mjs";
import { destinationOfApp } from "@studio/Helpers/destination-app-lookup";
import { isDceEditorRoute } from "@studio/Helpers/studio-dce-editor-route";
import {
  APP_BUILDER_SECTION,
  CREATE_AREA_APPS_SECTION,
  createAreaRailSection,
  ENGINE_NAVIGATE_EVENT,
} from "@studio/Helpers/app-builder-host";
import { defineStore } from "pinia";
import { canonicalPageSegment, canonicalStudioPath, pageSection, pathSegmentsForPage, SETTINGS_SEGMENT }
  from "@studio/Helpers/studio-route-segments";
import { useStudioMobileStore } from "@studio/Stores/studio-mobile-store";

export const STUDIO_NAVIGATE_EVENT = "studio:navigate";
const SETUP_PAGE = "setup";

export const useStudioNavigationStore = defineStore("studio-navigation-store", {
  state: () => {
    return {
      area: "",
      page: "",
      section: "",
      subPage: "",
      subSubPage: "",
      subPath: "",
      query: {},
      previousPageEditorUri: null,
      previousAppPagesUri: null,
    };
  },
  getters: {
    uriArea() {
      return this.area === "create" ? "studio" : this.area;
    },
    baseUri() {
      if (typeof window !== "undefined") {
        return `${window.location.origin}`;
      }
      return "";
    },
    activeDestinationId() {
      return this.area === "destinations" && /^\d+$/.test(this.section) ? Number(this.section) : 0;
    },
    isSetupPage() {
      return this.activeDestinationId > 0 && this.page === SETUP_PAGE;
    },
    isDceEditor() {
      return isDceEditorRoute({ area: this.area, page: this.page, subPage: this.subPage });
    },
    inAppBuilder() {
      return this.area === "create" && this.section === APP_BUILDER_SECTION && /^\d+$/.test(String(this.page ?? ""));
    },
    isAppBuilderSurface() {
      return this.area === "create" && (this.section === APP_BUILDER_SECTION || this.section === CREATE_AREA_APPS_SECTION);
    },
    builderAppId() {
      return this.inAppBuilder ? String(this.page) : "";
    },
    activePageSection() {
      if (this.inAppBuilder) {
        return this.subPage || "dashboard";
      }
      if (this.area === "create") {
        return createAreaRailSection(this.section);
      }
      return pageSection(this.page);
    },
    sidebarSection() {
      return this.query.sidebar || null;
    },
    queryString() {
      const pairs = Object.entries(this.query).filter(([, value]) => value !== null && value !== undefined);
      if (!pairs.length) {
        return "";
      }
      return `?${pairs.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value).replace(/%2C/g, ",")}`).join("&")}`;
    },
    areaSegment() {
      return this.area === "destinations" ? "build" : this.area;
    },
    currentPagePath() {
      const parts = [
        this.areaSegment,
        this.section,
        ...pathSegmentsForPage(this.page),
        this.subPage,
        this.subPath,
      ];
      return `/${parts.filter(Boolean).join("/")}`;
    },
    currentUri() {
      if (this.section === "users" && this.page === "users") {
        if (!this.subPage) {
          return "/users";
        }

        if (["applications", "invites", "invite"].includes(this.subPage)) {
          return `/users/${this.subPage}`;
        }
      }

      let area = this.area === "create" ? "studio" : this.area;

      const parts = [
        area,
        this.section,
        ...pathSegmentsForPage(this.page),
        this.subPage,
        this.subSubPage,
      ].filter(Boolean);
      return `${parts.length ? `/${parts.join("/")}` : "/"}${this.queryString}`;
    },
  },
  actions: {
    getRouteParts(uri) {
      const parts = (uri || "/").split("/").filter(Boolean);

      let area = "create";
      let index = 1;
      switch (parts[0]) {
        case "build":
        case "builder": {
          area = "destinations";
          break;
        }
        case "apps": {
          area = "apps";
          break;
        }
      }
      let section = parts[index] || null;
      let pageIndex = index + 1;

      if (parts[pageIndex] === SETTINGS_SEGMENT) {
        if (parts.length === pageIndex + 1) {
          return {
            area,
            section: section || "home",
            page: SETTINGS_SEGMENT,
            subPage: null,
            subSubPage: null,
            subPath: null,
          };
        }
        pageIndex += 1;
      }

      let page = canonicalPageSegment(parts[pageIndex] || null);
      let subPage = parts[pageIndex + 1] || null;
      let subSubPage = parts[pageIndex + 2] || null;
      let subPath = parts.slice(pageIndex + 2).join("/") || null;

      if (!section) {
        section = "home";
      }

      return {
        area,
        section,
        page,
        subPage,
        subSubPage,
        subPath,
      };
    },
    historyState() {
      return { storeData: { ...this.$state, query: { ...this.query } } };
    },
    parseQuery(search) {
      const params = new URLSearchParams(search || "");
      const parsed = {};
      params.forEach((value, key) => {
        parsed[key] = value;
      });
      return parsed;
    },
    syncFromLocation() {
      const rawPathname = typeof window === "undefined" ? "/" : window.location.pathname;
      const locationSearch = typeof window === "undefined" ? "" : window.location.search;
      const locationPathname = toDestinationAppPath(canonicalStudioPath(rawPathname), destinationOfApp);

      if (locationPathname !== rawPathname) {
        window.history.replaceState(window.history.state, "", `${locationPathname}${locationSearch}`);
      }

      const route = this.getRouteParts(locationPathname);

      this.area = route.area;
      this.section = route.section;
      this.page = route.page;
      this.subPage = route.subPage;
      this.subSubPage = route.subSubPage;
      this.subPath = route.subPath;
      this.query = this.parseQuery(locationSearch);
    },
    navigateToUri(requestedUri) {
      if (!requestedUri || requestedUri[0] !== "/") {
        throw new Error("Uri must start with a slash");
      }
      const uri = toDestinationAppPath(requestedUri, destinationOfApp);

      const previousLocationPathname = typeof window === "undefined" ? "" : window.location.pathname;
      const pageEditorPattern = /^\/(?:build|builder)\/\d+\/pages\/[^/]+$/;
      const componentEditorPattern = /^\/(?:build|builder)\/\d+\/components\/[^/]+$/;
      if (pageEditorPattern.test(previousLocationPathname) && componentEditorPattern.test(uri)) {
        this.previousPageEditorUri = previousLocationPathname;
      } else if (pageEditorPattern.test(uri)) {
        this.previousPageEditorUri = null;
      }

      const appPagesPattern = /^\/(?:build|builder)\/\d+\/apps\/[^/]+\/pages$/;
      if (pageEditorPattern.test(uri)) {
        if (appPagesPattern.test(previousLocationPathname)) {
          this.previousAppPagesUri = previousLocationPathname;
        } else if (!componentEditorPattern.test(previousLocationPathname)) {
          this.previousAppPagesUri = null;
        }
      }

      useStudioMobileStore().clearStickyDrillPeel();

      window.history.pushState({}, "", uri);
      this.syncFromLocation();
      window.history.replaceState(this.historyState(), "", uri);
      window.dispatchEvent(new CustomEvent(STUDIO_NAVIGATE_EVENT, { detail: { uri } }));
    },
    writeQuery(patch, historyMethod) {
      const query = { ...this.query };
      for (const [key, value] of Object.entries(patch)) {
        if (value === null || value === undefined) {
          delete query[key];
        } else {
          query[key] = String(value);
        }
      }
      this.query = query;
      const uri = `${window.location.pathname}${this.queryString}${window.location.hash}`;
      window.history[historyMethod](this.historyState(), "", uri);
    },
    replaceQuery(patch) {
      this.writeQuery(patch, "replaceState");
    },
    pushQuery(patch) {
      this.writeQuery(patch, "pushState");
    },
    replaceUri(uri) {
      if (!uri || uri[0] !== "/") {
        throw new Error("Uri must start with a slash");
      }
      window.history.replaceState(window.history.state, "", uri);
      this.syncFromLocation();
      window.history.replaceState(this.historyState(), "", uri);
      window.dispatchEvent(new CustomEvent(STUDIO_NAVIGATE_EVENT, { detail: { uri } }));
    },
    popState(event) {
      if (!event.state || !event.state.storeData) {
        this.syncFromLocation();
        return;
      }
      const { query, ...route } = event.state.storeData;
      this.$patch(route);
      this.query = { ...query };
    },
    followEngineNavigation() {
      this.syncFromLocation();
      window.history.replaceState({ ...window.history.state, ...this.historyState() }, "", window.location.href);
      window.dispatchEvent(new CustomEvent(STUDIO_NAVIGATE_EVENT, { detail: { uri: window.location.pathname + window.location.search } }));
    },
    initialize() {
      this.syncFromLocation();
      window.history.replaceState(this.historyState(), "", window.location.href);
      window.addEventListener("popstate", this.popState);
      window.addEventListener(ENGINE_NAVIGATE_EVENT, this.followEngineNavigation);
    },
  },
});
